<template>
    <div class="jewellery-photo-preview mt-3" v-if="preview">
        <img
            :src="preview"
            alt="Selected jewellery photo"
            class="rounded shadow"
            style="max-width: 250px; height: auto"
        />
        <p class="text-xs my-2 text-gray-600" v-if="restored">
            This is the photo you selected. Please choose the file again before
            resubmitting.
        </p>
    </div>
</template>

<script>
// Persist the jewellery photo preview across a server-side validation failure
// (FCI-53). The certificate form is a standard POST form: when validation
// fails the page reloads, and a file <input> cannot be re-populated for
// browser-security reasons, so the chosen photo "disappears". This component
// stashes a small preview of the selected image and shows it again after the
// reload, so the user can see what they had selected.
const STORAGE_KEY = "fci53_jewellery_photo_preview";
// Cap the stored preview so it comfortably fits sessionStorage (~5MB) even for
// large phone photos — a thumbnail is all the preview needs.
const MAX_DIMENSION = 500;

export default {
    props: {
        // name attribute of the existing file input to observe.
        inputName: {
            type: String,
            default: "jewellery_photo",
        },
        // Whether the page rendered with validation errors (i.e. this is a
        // re-render after a failed submit). Passed from Blade ($errors->any()).
        hasErrors: {
            type: [Boolean, String],
            default: false,
        },
    },
    data() {
        return {
            preview: "",
            restored: false,
            input: null,
        };
    },
    computed: {
        formHasErrors() {
            return this.hasErrors === true || this.hasErrors === "true";
        },
    },
    mounted() {
        this.input = document.querySelector(
            'input[type="file"][name="' + this.inputName + '"]'
        );
        if (this.input) {
            this.input.addEventListener("change", this.onChange);
        }

        if (this.formHasErrors) {
            // Re-render after a failed submit — restore the stashed preview.
            this.restore();
        } else {
            // Fresh form load — drop any preview left over from a previous
            // attempt so it never shows on an unrelated new certificate.
            this.clearStored();
        }
    },
    beforeDestroy() {
        if (this.input) {
            this.input.removeEventListener("change", this.onChange);
        }
    },
    methods: {
        onChange(event) {
            const file =
                event.target.files && event.target.files[0]
                    ? event.target.files[0]
                    : null;
            this.restored = false;

            if (!file || !file.type || file.type.indexOf("image/") !== 0) {
                this.preview = "";
                this.clearStored();
                return;
            }
            this.buildPreview(file);
        },
        buildPreview(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    this.preview = this.downscale(img);
                    this.store();
                };
                img.onerror = () => {
                    // Not a decodable image — show nothing rather than break.
                    this.preview = "";
                    this.clearStored();
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        downscale(img) {
            let width = img.width;
            let height = img.height;
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                const scale = Math.min(
                    MAX_DIMENSION / width,
                    MAX_DIMENSION / height
                );
                width = Math.round(width * scale);
                height = Math.round(height * scale);
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            // JPEG keeps the stored data URL small enough for sessionStorage.
            return canvas.toDataURL("image/jpeg", 0.8);
        },
        store() {
            try {
                sessionStorage.setItem(STORAGE_KEY, this.preview);
            } catch (e) {
                // sessionStorage may be unavailable/full; the preview still
                // shows for the current view, it just won't survive a reload.
            }
        },
        restore() {
            try {
                const saved = sessionStorage.getItem(STORAGE_KEY);
                if (saved) {
                    this.preview = saved;
                    this.restored = true;
                }
            } catch (e) {
                // ignore
            }
        },
        clearStored() {
            try {
                sessionStorage.removeItem(STORAGE_KEY);
            } catch (e) {
                // ignore
            }
        },
    },
};
</script>
